/* eslint-disable react-hooks/exhaustive-deps */
import { Flex, Icon, Option, Select } from '@tonic-one/react';
import { DeleteIcon } from '@tonic-one/react-icons';
import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ActionEnum,
  ComplianceRuleType,
  DlpPolicyItem,
  DlpTemplateDetail,
  TableColumn,
} from 'src/pages/Policy/utils/types';
import {
  storeSelector,
  templateSelector,
  usePolicyContext,
} from '@Policy/store';
import { dlpTemplateDetail } from 'src/pages/Policy/TemplateConfig/cfg.dlp.default';
import cloneDeep from 'lodash/cloneDeep';

import { PolicyApi } from '@Apis';
import isEqual from 'lodash/isEqual';
import i18next from 'i18next';
import { useDlpConditionStatementStyle } from '../Filters/Dlp/style';
import { actionTranslateMap } from '@Policy/AdvancedThreatProtection/PolicyTemplate/Components/shared';
import {
  tmDpfilterrulesSelector,
  tmMipLabelRulesSelector,
} from '@Policy/store/filters/dlp';
import { useGlobalSettingStore } from '@Models/useGlobalSettingModel';
import { PolicyTypeEnum } from '@Policy/utils/constants';

const defaultDlpTemplateDetail: DlpTemplateDetail =
  cloneDeep(dlpTemplateDetail);

export const useDlpTemplateImport = () => {
  // import
  const [isImportModalVisible, setIsImportModalVisible] = React.useState(false);
  const handleImportModalClose = React.useCallback(() => {
    setIsImportModalVisible(false);
  }, []);
  const handleImportModalOpen = React.useCallback(() => {
    setIsImportModalVisible(true);
  }, []);
  const handleDlpTemplatesImport = React.useCallback((entries: any) => {
    console.log('import dlp templates', entries);
    // TODO: import dlp templates
    // importDlpTemplates(entries as string[]);
    setIsImportModalVisible(false);
  }, []);
  return {
    isImportModalVisible,
    handleImportModalClose,
    handleImportModalOpen,
    handleDlpTemplatesImport,
  };
};

export const useDlpTemplateAddEdit = (props: {
  setIsLoading: (status: boolean) => void;
}) => {
  const { t } = useTranslation();
  const { setIsLoading } = props;
  // get store from context
  const contextStore = usePolicyContext();
  // get store selector
  const contextSelector = storeSelector(contextStore);
  // get action from store
  const getDlpTemplates = contextSelector((s) => s.getDlpTemplates);
  // add
  const [isAddModalVisible, setIsAddModalVisible] = React.useState(false);
  const [dlpTemplateDetail, setDlpTemplateDetail] =
    React.useState<DlpTemplateDetail>(defaultDlpTemplateDetail);
  const handleAddModalClose = React.useCallback(() => {
    setIsAddModalVisible(false);
  }, []);
  const handleAddModalOpen = React.useCallback(async (templateId = '') => {
    try {
      setIsLoading(true);
      if (!templateId) {
        setDlpTemplateDetail(defaultDlpTemplateDetail);
      } else {
        const resp = await PolicyApi.getDlpTemplateById(
          templateId,
          i18next.language
        );
        const { data: dlpTemplateDetail } = resp;
        setDlpTemplateDetail(dlpTemplateDetail);
      }
      setIsAddModalVisible(true);
    } catch (error) {
      console.error(error);
      window.showNotification({
        titleMsg: t('common.apiException.internalError'),
        severity: 'error',
      });
    } finally {
      setIsLoading(false);
    }
  }, []);
  const handleAddModalSave = React.useCallback(async (values: any) => {
    console.log('add dlp template', values);
    try {
      if (!values.cfgIndex) {
        // add
        await PolicyApi.addDlpTemplate(values);
      } else {
        // update
        await PolicyApi.updateDlpTemplateById(values.cfgIndex, values);
      }
    } catch (error: any) {
      const code = error?.status || 'Unknown';
      if (code === 400 && error.data.error.details[0].code === -118) {
        window.showNotification({
          severity: 'error',
          titleMsg: t('errors.policy.POLICY_API;UPSERT_TEMPLATE_FAILURE;-118'),
        });
      } else {
        window.showNotification({
          severity: 'error',
          titleMsg: t('common.apiException.internalError'),
        });
      }
    } finally {
      await getDlpTemplates();
    }
  }, []);
  return {
    isAddModalVisible,
    handleAddModalClose,
    handleAddModalOpen,
    handleAddModalSave,
    dlpTemplateDetail,
  };
};
export const useSelectedTalbeStyle = (mipShow: boolean) => {
  const { tableStyleProps, tableStylePropsWithMip } =
    useDlpConditionStatementStyle();
  const selectedDlpTemplatesTableStyleProps = useMemo(() => {
    return mipShow ? tableStylePropsWithMip : tableStyleProps;
  }, [mipShow]);
  return {
    selectedDlpTemplatesTableStyleProps,
  };
};

export const useSelectedDlpTemplateColumns = () => {
  const { t } = useTranslation();
  const { selectedOrgGlobalSetting } = useGlobalSettingStore();
  const gmailQtSettingFeatureShow =
    !!selectedOrgGlobalSetting?.gmailQtSettingFeatureShow;
  const mipFeatureShow = !!selectedOrgGlobalSetting?.mipFeatureShow;
  // get store from context
  const contextStore = usePolicyContext();
  // get store selector
  const contextSelector = storeSelector(contextStore);
  const policySelector = templateSelector(contextStore)<DlpPolicyItem>;
  // get action from store
  const policyType = policySelector((s) => s.policyType);
  const setDlpTemplateAction = contextSelector((s) => s.setDlpTemplateAction);
  const setMipLabelAction = contextSelector((s) => s.setMipLabelAction);
  const deleteDlpTemplate = contextSelector((s) => s.deleteDlpTemplate);
  const deleteMipLabel = contextSelector((s) => s.deleteMipLabel);
  // local state
  // dlp template action options
  let actionOptions: ActionEnum[] = [];
  // ExchangeOnline
  const actionOptionsExchange = [
    ActionEnum.pass,
    ActionEnum.delete,
    ActionEnum.quarantine,
    ActionEnum.moveToJunkEmailFolder,
  ];
  //GmailInline
  const actionOptionsGmailInline = [ActionEnum.pass, ActionEnum.quarantine];
  // Exchange inline
  const actionOptionsExchangeInline = [
    ActionEnum.pass,
    ActionEnum.quarantine,
    ActionEnum.changeRecipient,
  ];
  // Gmail
  const actionOptionsGmail = gmailQtSettingFeatureShow
    ? [
        ActionEnum.pass,
        ActionEnum.delete,
        ActionEnum.quarantine,
        ActionEnum.labelEmail,
      ]
    : [ActionEnum.pass, ActionEnum.delete, ActionEnum.labelEmail];
  // Teams, OneDrive, SharePoint
  const actionOptionsTeams = mipFeatureShow
    ? [
        ActionEnum.pass,
        ActionEnum.delete,
        ActionEnum.quarantine,
        ActionEnum.applySensitivityLabel,
        ActionEnum.removeSensitivityLabel,
      ]
    : [ActionEnum.pass, ActionEnum.delete, ActionEnum.quarantine];
  // TeamsChat
  const actionOptionsTeamsChat = [ActionEnum.pass, ActionEnum.block];
  // Dropbox, Box
  const actionOptionsDropbox = [
    ActionEnum.pass,
    ActionEnum.delete,
    ActionEnum.quarantine,
  ];
  // mip action options
  let mipActionOptions: ActionEnum[] = [];
  // ExchangeOnline  Gmail  Teams OneDrive SharePoint
  const mipActionOptionsDefault = [
    ActionEnum.pass,
    ActionEnum.delete,
    ActionEnum.quarantine,
  ];

  const mipActionOptionsExchangeOnline = [
    ActionEnum.pass,
    ActionEnum.delete,
    ActionEnum.quarantine,
    ActionEnum.moveToJunkEmailFolder,
  ];

  // ExchangeInline  GmailInline T
  const mipActionOptionsInline = [ActionEnum.pass, ActionEnum.quarantine];
  // Teams Chat
  const mipActionOptionsTeamsChat = [ActionEnum.pass, ActionEnum.block];
  // Google Drive
  const actionOptionsGoogleDrive = [
    ActionEnum.pass,
    ActionEnum.delete,
    ActionEnum.quarantine,
  ];
  mipActionOptions = mipActionOptionsDefault;
  if (policyType === PolicyTypeEnum.TeamsChat) {
    mipActionOptions = mipActionOptionsTeamsChat;
  } else if (
    policyType === PolicyTypeEnum.ExchangeInline ||
    policyType === PolicyTypeEnum.GmailInline
  ) {
    mipActionOptions = mipActionOptionsInline;
  } else if (policyType === PolicyTypeEnum.Exchange) {
    mipActionOptions = mipActionOptionsExchangeOnline;
  }
  switch (policyType) {
    case PolicyTypeEnum.Exchange:
      actionOptions = actionOptionsExchange;
      break;
    case PolicyTypeEnum.GmailInline:
      actionOptions = actionOptionsGmailInline;
      break;
    case PolicyTypeEnum.ExchangeInline:
      actionOptions = actionOptionsExchangeInline;
      break;
    case PolicyTypeEnum.Gmail:
      actionOptions = actionOptionsGmail;
      break;
    case PolicyTypeEnum.Teams:
    case PolicyTypeEnum.OneDrive:
    case PolicyTypeEnum.SharePoint:
      actionOptions = actionOptionsTeams;
      break;
    case PolicyTypeEnum.TeamsChat:
      actionOptions = actionOptionsTeamsChat;
      break;
    case PolicyTypeEnum.Dropbox:
    case PolicyTypeEnum.Box:
      actionOptions = actionOptionsDropbox;
      break;
    case PolicyTypeEnum.GoogleDrive:
    case PolicyTypeEnum.SalesforceSandbox:
    case PolicyTypeEnum.SalesforceProduction:
      actionOptions = actionOptionsGoogleDrive;
      break;
    default:
      break;
  }

  const modifyActionOptions = useCallback(
    (actionOptions: ActionEnum[], selectedAction: ActionEnum) => {
      const newActions: ActionEnum[] = [...actionOptions];
      if (
        policyType === PolicyTypeEnum.GmailInline ||
        policyType === PolicyTypeEnum.ExchangeInline
      ) {
        if (selectedAction === ActionEnum.delete) {
          newActions.push(ActionEnum.delete);
        }
      }
      return newActions;
    },
    [policyType]
  );

  function actionHandle(item: any) {
    if (policyType === PolicyTypeEnum.Gmail) {
      if (
        item.dpAction === ActionEnum.quarantine &&
        !actionOptions.includes(item.dpAction)
      ) {
        return ActionEnum.labelEmail;
      }
    }
    return item.dpAction;
  }

  const selectedDlpTemplateColumns = React.useMemo(() => {
    const columns: TableColumn[] = [
      {
        id: 'templateName',
        header: t('policy.dlp.header.templateName'),
        accessorKey: 'templateName',
        size: 206,
        style: {
          lineHeight: '30px',
        },
      },
      {
        id: 'dpAction',
        header: t('policy.dlp.header.dpAction'),
        cell: (row: any) => {
          return (
            <Flex alignItems="center">
              <Select
                value={actionHandle(row)}
                onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                  if (row.type === ComplianceRuleType.dlpTemplate) {
                    setDlpTemplateAction(row.id, Number(e.target.value));
                  } else {
                    setMipLabelAction(row.id, Number(e.target.value));
                  }
                }}
                width="140px"
              >
                {row.type === ComplianceRuleType.dlpTemplate
                  ? modifyActionOptions(actionOptions, row.dpAction).map(
                      (action) => (
                        <Option value={action} key={action}>
                          {t(actionTranslateMap[action])}
                        </Option>
                      )
                    )
                  : modifyActionOptions(mipActionOptions, row.dpAction).map(
                      (action) => (
                        <Option value={action} key={action}>
                          {t(actionTranslateMap[action])}
                        </Option>
                      )
                    )}
              </Select>
              <Icon
                as={DeleteIcon}
                ml="8px"
                onClick={() => {
                  if (row.type === ComplianceRuleType.dlpTemplate) {
                    deleteDlpTemplate(row.id);
                  } else {
                    deleteMipLabel(row.id);
                  }
                }}
              />
            </Flex>
          );
        },
        size: 268,
      },
    ];
    return columns;
  }, []);
  return {
    selectedDlpTemplateColumns,
  };
};

export const useSelectedDlpTemplateRows = (
  mipShow: boolean,
  selectedDlpTemplateColumns: TableColumn[]
) => {
  // get store from context
  const contextStore = usePolicyContext();
  // get store selector
  const contextSelector = storeSelector(contextStore);
  // const policySelector = templateSelector(contextStore);
  // get state from store
  const tmDpfilterrules = contextSelector(tmDpfilterrulesSelector, isEqual);
  const tmMipLabelRules = contextSelector(tmMipLabelRulesSelector, isEqual);
  // get action from store

  const dlpRows = React.useMemo(() => {
    let result;
    const dlpTemplates = tmDpfilterrules.map((row, rowIndex) => {
      return {
        id: `${ComplianceRuleType.dlpTemplate}-${rowIndex}`,
        cells: selectedDlpTemplateColumns.map(
          (column: TableColumn, columnIndex: number) => {
            return {
              id: `${columnIndex}_${column.id}`,
              column,
              row,
            };
          }
        ),
        row,
      };
    });
    result = dlpTemplates;
    if (mipShow) {
      const mipLabels = tmMipLabelRules.map((row, rowIndex) => {
        return {
          id: `${ComplianceRuleType.mipLabel}-${rowIndex}`,
          cells: selectedDlpTemplateColumns.map(
            (column: TableColumn, columnIndex: number) => {
              return {
                id: `${columnIndex}_${column.id}`,
                column,
                row,
              };
            }
          ),
          row,
        };
      });
      result = [...dlpTemplates, ...mipLabels];
    }
    return result;
  }, [selectedDlpTemplateColumns, tmDpfilterrules, tmMipLabelRules, mipShow]);
  return {
    dlpRows,
  };
};

export const useComplianceRulesInit = (
  mipShow: boolean,
  setIsLoading: (status: boolean) => void
) => {
  // get store from context
  const contextStore = usePolicyContext();
  // get store selector
  const contextSelector = storeSelector(contextStore);
  // get state from store
  // get action from store
  const getDlpTemplates = contextSelector((s) => s.getDlpTemplates);
  const getMipLabels = contextSelector((s) => s.getMipLabels);
  const handleInit = React.useCallback(() => {
    setIsLoading(true);
    const funcArr = [getDlpTemplates()];
    if (mipShow) {
      funcArr.push(getMipLabels());
    }
    Promise.all(funcArr).then(() => {
      setIsLoading(false);
    });
  }, [mipShow]);
  return {
    handleInit,
  };
};
